import * as Sentry from '@sentry/nextjs';
import { type Arguments, Payment } from 'payments-lib-types';
import { withRetry } from '@/modules/common';
import { sendDepositPaymentMethodsFailedLoading } from '@/modules/googleAnalytics';

interface GetMethodsArgs
  extends Omit<Arguments.GetMethodsArgs, 'paymentAction'> {
  paymentAction?: Payment.Action;
}

export const fetchPaymentMethods = async ({
  currency,
  paymentAction = Payment.Action.Deposit,
}: GetMethodsArgs) => {
  // Check if the action is a deposit
  const isDeposit = paymentAction === Payment.Action.Deposit;
  // Access the PaymentsAPI object from the global window scope
  const PaymentsAPI = window.PaymentsAPI;

  // If PaymentsAPI is not available, report an error (for deposits) and return empty data
  if (!PaymentsAPI) {
    if (isDeposit) {
      sendDepositPaymentMethodsFailedLoading({ error: 'no_payments_lib' });
    }
    return { data: [] };
  }

  try {
    // Call PaymentsAPI.getMethods with retry logic (2 attempts, 500ms delay = max 0.5s wait)
    const response = await withRetry(
      () => PaymentsAPI.getMethods({ currency, paymentAction }),
      2,
      500
    );

    // If it's a deposit and response is empty, report an error with the empty value
    if (isDeposit && !(response && response.length)) {
      sendDepositPaymentMethodsFailedLoading({
        error: 'empty_value',
        value: JSON.stringify(response),
      });
    }

    // Return fetched payment methods
    return { data: response };
  } catch (error) {
    // Log error and report it (for deposits)
    console.log('[PaymentsAPI.getMethods][withRetry] error', error);
    if (isDeposit) {
      sendDepositPaymentMethodsFailedLoading({
        error: error instanceof Error ? error.message : JSON.stringify(error),
      });
    }
    // Return empty data on failure
    return { data: [] };
  }
};

interface GetMethodFieldsArgs
  extends Omit<Arguments.GetMethodFieldsArgs, 'paymentAction'> {
  paymentAction?: Payment.Action;
}

export const getMethodFields = (args: GetMethodFieldsArgs) => {
  const PaymentsAPI = window.PaymentsAPI;
  if (!PaymentsAPI) return Promise.reject({ data: undefined });

  return PaymentsAPI.getMethodFields({
    ...args,
    paymentAction: args.paymentAction ?? Payment.Action.Deposit,
  })
    .then((res) => ({ data: res }))
    .catch((error) => {
      Sentry.captureException(JSON.stringify(error), {
        tags: {
          category: 'deposit',
          pathToFile: 'src/helpers/payments.ts',
        },
        level: 'error',
      });

      Sentry.captureMessage(
        `Deposit request error for currency ${args.currency}, id ${args.id}`,
        'error'
      );
      return { data: error };
    });
};

export const savePlayerFields = (args: Arguments.SavePlayerFieldsArgs) =>
  window.PaymentsAPI?.savePlayerFields(args).then((res) => ({ data: res })) ||
  Promise.reject('[savePlayerFields]: window.PaymentsAPI is not defined');

interface SubmitArgs extends Omit<Arguments.SubmitArgs, 'paymentAction'> {
  paymentAction?: Payment.Action;
}

export const submitFormPayment = (args: SubmitArgs) => {
  const PaymentsAPI = window.PaymentsAPI;
  if (!PaymentsAPI) return Promise.reject({ data: undefined });

  return PaymentsAPI.submit({
    ...args,
    paymentAction: args.paymentAction ?? Payment.Action.Deposit,
  })
    .then((res) => ({ data: res }))
    .catch((error) => {
      Sentry.captureMessage(
        `Deposit request error for currency ${args.currency}, id ${args.id}`,
        'error'
      );
      Sentry.captureException(
        JSON.stringify({ error, cvv: args.methodFieldsData.encCvv }),
        {
          tags: {
            category: 'deposit',
            pathToFile: 'src/helpers/payments.ts',
          },
          level: 'error',
        }
      );
      return { data: error };
    });
};

export const resetCachePayments = () => {
  const PaymentsAPI = window.PaymentsAPI;
  if (!PaymentsAPI) return Promise.reject();

  return PaymentsAPI.resetCache().catch((error) => {
    Sentry.captureMessage(
      'Error with method window.PaymentsAPI.resetCache.',
      'warning'
    );
    Sentry.captureException(JSON.stringify(error), {
      tags: {
        category: 'deposit',
        pathToFile: 'src/helpers/payments.ts',
      },
      level: 'error',
    });
  });
};

interface DeleteSavedProfileArgs
  extends Omit<Arguments.DeleteSavedProfileArgs, 'paymentAction'> {
  paymentAction?: Payment.Action;
}

export const deleteSavedPaymentMethod = (args: DeleteSavedProfileArgs) => {
  const PaymentsAPI = window.PaymentsAPI;
  if (!PaymentsAPI) return Promise.resolve({ data: undefined });

  return PaymentsAPI.deleteSavedProfile({
    ...args,
    paymentAction: args.paymentAction ?? Payment.Action.Deposit,
  })
    .then(() => ({ data: undefined }))
    .catch((error) => {
      Sentry.withScope((scope) => {
        scope.setTags({
          category: 'deposit',
          pathToFile: 'src/helpers/payments.ts',
        });

        scope.setLevel('error');

        Sentry.captureMessage(
          `Delete saved profile request error for profile id ${args.savedProfileId}`
        );
      });

      return { data: error };
    });
};
